"use client";

import { useEffect, useRef, useState } from "react";
import { useTheme } from "next-themes";
import { useSession } from "next-auth/react";
import { Monitor, Sun, Moon, Globe, ChevronUp, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/lib/i18n/context";

/**
 * Footer theme + language controls, styled after cursor.com's footer: a
 * segmented system/light/dark switcher and a language menu that opens upward.
 * Built for the dark footer surface (#2A2620).
 */

const THEME_OPTIONS: Array<{ value: "system" | "light" | "dark"; Icon: typeof Monitor; labelSl: string; labelEn: string }> = [
  { value: "system", Icon: Monitor, labelSl: "Sistem", labelEn: "System" },
  { value: "light", Icon: Sun, labelSl: "Svetla", labelEn: "Light" },
  { value: "dark", Icon: Moon, labelSl: "Temna", labelEn: "Dark" },
];

const LANGUAGES: Array<{ code: "en" | "sl"; label: string }> = [
  { code: "en", label: "English" },
  { code: "sl", label: "Slovenščina" },
];

export function FooterControls() {
  const { theme, setTheme } = useTheme();
  const { lang, setLang } = useTranslation();
  // Logged-in users manage their language exclusively in Settings → Language,
  // so the public-footer language switcher is hidden once authenticated.
  const { status } = useSession();
  const isLoggedIn = status === "authenticated";
  const isSl = lang !== "en";

  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const currentLangLabel = isSl ? "Slovenščina" : "English";

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Theme — segmented system / light / dark */}
      <div
        role="group"
        aria-label={isSl ? "Tema vmesnika" : "Interface theme"}
        className="inline-flex items-center gap-1 rounded-full border border-[#4A4641] bg-[#221E19] p-1"
      >
        {THEME_OPTIONS.map(({ value, Icon, labelSl, labelEn }) => {
          const active = mounted && theme === value;
          const label = isSl ? labelSl : labelEn;
          return (
            <button
              key={value}
              type="button"
              aria-label={label}
              aria-pressed={active}
              title={label}
              disabled={!mounted}
              onClick={() => setTheme(value)}
              className={cn(
                "inline-flex h-8 w-8 items-center justify-center rounded-full transition disabled:opacity-50",
                active
                  ? "bg-[#3A3630] text-[#F2EEE8] shadow-[inset_0_0_0_1px_#5A554E]"
                  : "text-[#8A867E] hover:text-[#F2EEE8]",
              )}
            >
              <Icon className="h-4 w-4" />
            </button>
          );
        })}
      </div>

      {/* Language — menu opens upward. Hidden for logged-in users (they switch
          language in Settings). */}
      {!isLoggedIn && (
      <div className="relative" ref={menuRef}>
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-2 rounded-full border border-[#4A4641] bg-[#221E19] px-4 py-[7px] text-sm text-[#F2EEE8] transition hover:border-[#5A554E]"
        >
          <Globe className="h-4 w-4 text-[#C9C4BD]" />
          {currentLangLabel}
          <ChevronUp className={cn("h-3.5 w-3.5 text-[#C9C4BD] transition-transform", open ? "" : "rotate-180")} />
        </button>

        {open && (
          <div
            role="listbox"
            className="absolute bottom-full right-0 mb-2 min-w-[180px] rounded-xl border border-[#4A4641] bg-[#221E19] p-1 shadow-[0_12px_28px_rgba(0,0,0,0.45)]"
          >
            {LANGUAGES.map((l) => {
              const selected = lang === l.code;
              return (
                <button
                  key={l.code}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    setLang(l.code);
                    setOpen(false);
                  }}
                  className="flex w-full items-center justify-between gap-4 rounded-lg px-3 py-2 text-sm text-[#F2EEE8] transition hover:bg-[#3A3630]"
                >
                  <span>{l.label}</span>
                  {selected && <Check className="h-4 w-4 text-[var(--terracotta)]" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
